import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Leaf, Search } from "lucide-react";
import { MENU } from "../data/menu";
import { FadeUp, SectionHead } from "./Motion";

const Spice = ({ level }) => (
  <span className="ml-2 inline-flex items-center gap-0.5 align-middle" title={`Spice level ${level}`}>
    {Array.from({ length: level }).map((_, i) => (
      <Flame key={i} size={12} className="fill-gold/20 text-gold" />
    ))}
  </span>
);

const Item = ({ item }) => (
  <div className="py-4">
    <div className="flex items-baseline">
      <h4 className="font-serif text-xl text-white/95">
        {item.name}
        {item.spice ? <Spice level={item.spice} /> : null}
      </h4>
      <span className="dot-leader" />
      <span className="font-serif text-lg text-gold">${item.price}</span>
    </div>
    {item.desc ? <p className="mt-1.5 max-w-md text-sm leading-relaxed text-mist">{item.desc}</p> : null}
    {item.tag ? (
      <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold">
        <Leaf size={10} /> {item.tag}
      </span>
    ) : null}
  </div>
);

const MenuBrowser = () => {
  const [active, setActive] = useState(MENU[0].id);
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return MENU.flatMap((c) =>
      c.items
        .filter((it) => it.name.toLowerCase().includes(q) || (it.desc || "").toLowerCase().includes(q))
        .map((it) => ({ ...it, category: c.label }))
    );
  }, [query]);

  const category = MENU.find((c) => c.id === active);

  return (
    <section id="full-menu" data-testid="full-menu-section" className="border-y border-white/8 bg-surface/40">
      <div className="mx-auto max-w-7xl px-5 py-32 md:px-10 md:py-40">
        <SectionHead
          index="03"
          kicker="Full Menu · 130+ Items"
          title={<>Every craving, <em className="italic text-gold">answered</em></>}
        />

        <FadeUp className="mb-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="menu-category-tabs">
            {MENU.map((c) => (
              <button
                key={c.id}
                data-testid={`menu-tab-${c.id}`}
                onClick={() => { setActive(c.id); setQuery(""); }}
                className={`whitespace-nowrap rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                  active === c.id && !query
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-white/12 text-white/55 hover:border-gold/50 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative shrink-0">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="menu-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 130+ dishes…"
              className="w-full rounded-full border border-white/12 bg-transparent py-3 pl-11 pr-5 text-sm text-white placeholder:text-white/35 focus:border-gold focus:outline-none md:w-72"
            />
          </div>
        </FadeUp>

        <AnimatePresence mode="wait">
          <motion.div
            key={query ? "search" : active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            {results ? (
              <div data-testid="menu-search-results">
                <p className="mb-6 text-[12px] uppercase tracking-[0.25em] text-gold">
                  {results.length} result{results.length === 1 ? "" : "s"} for “{query}”
                </p>
                <div className="grid grid-cols-1 gap-x-14 md:grid-cols-2">
                  {results.map((it, i) => <Item key={`${it.name}-${i}`} item={it} />)}
                </div>
                {results.length === 0 && (
                  <p className="py-16 text-center font-serif text-2xl text-white/40">No dishes found — try “dosa” or “coffee”.</p>
                )}
              </div>
            ) : (
              <div data-testid="menu-category-panel">
                <p className="mb-6 font-serif text-lg italic text-white/50">{category.note}</p>
                <div className="grid grid-cols-1 gap-x-14 md:grid-cols-2">
                  {category.items.map((it) => <Item key={it.name} item={it} />)}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <FadeUp className="mt-14">
          <p className="text-center text-[12px] uppercase tracking-[0.22em] text-white/40">
            Jain & Swaminarayan options available · 18% gratuity for parties of 4+ · Prices subject to change
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

export default MenuBrowser;
